import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { AgGridReact } from '@ag-grid-community/react'
import { statusPanel, aggFuncs, createChartContainer } from './pivotUtilities'
import TableActions from './components/TableActions'
import {
  GridCore,
  ClientSideRowModelModule,
  CsvExportModule
} from '@ag-grid-community/all-modules'
import {
  ExcelExportModule,
  RowGroupingModule,
  ServerSideRowModelModule,
  MasterDetailModule,
  MenuModule,
  SetFilterModule,
  ColumnsToolPanelModule,
  RangeSelectionModule,
  GridChartsModule,
  SideBarModule,
  StatusBarModule,
  FiltersToolPanelModule
} from '@ag-grid-enterprise/all-modules'
import classNames from 'classnames'

import { useThemeVariable } from '../Theme'

import '@ag-grid-community/core/dist/styles/ag-grid.min.css'

import TableHeader from './TableHeader'
import { autoSizeAll } from './helpers'

import './index.scss'
import './license'

const modules = [
  GridCore,
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  CsvExportModule,
  MasterDetailModule,
  ExcelExportModule,
  RowGroupingModule,
  MenuModule,
  ColumnsToolPanelModule,
  SetFilterModule,
  RangeSelectionModule,
  GridChartsModule,
  SideBarModule,
  StatusBarModule,
  FiltersToolPanelModule
]

function DataTable({
  groupDefaultExpanded,
  rowSelection,
  rowMultiSelectWithClick,
  editType,
  domLayout,
  columnDefs,
  rowData,
  rowHeight,
  enableRangeSelection,
  headerTooltip,
  headerClass,
  frameworkComponents,
  getRowStyle,
  sortingOrder,
  multiSortKey,
  animateRows,
  pagination,
  masterDetail,
  treeData,
  paginationPageSize,
  sortable,
  filter,
  floatingFilter,
  editable,
  canAddRows,
  canDeleteRows,
  canDuplicate,
  canClearAll,
  canShowCheckbox,
  canPinRowTop,
  canPinRowBottom,
  canSelectAll,
  canSearch,
  onSelectionChanged,
  onRowsSelect: onRowSelectedProp,
  onRowSelected,
  onRowAdd,
  onRowDelete,
  detailCellRendererParams,
  onRowsDuplicate,
  onAllRowsDelete,
  onFullRowEdit,
  colResize,
  resizable,
  fullRowEdit,
  width,
  minWidth,
  autoSizeColumns,
  flex,
  canExportCSV,
  isSizeToFit,
  isAutoSize,
  isAutoSizeAll,
  customActions,
  extraHeaderComponent,
  extraHeaderComponentContainerClassName,
  defaultColDef,
  className,
  minHeight,
  localeTextFunc,
  defaultActionsi18nKeys,
  autoSizeColumnsOnFirstRender,
  onGridReady: onGridReadyProp,
  onFirstDataRendered: onFirstDataRenderedProp,
  enablePivot,
  maxHeightWithScroll,
  ...props
}) {
  const agTheme = useThemeVariable('agTheme')
  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)

  if (enablePivot && !enablePivot?.allFeature) {
    enablePivot.tableProps &&
      Object.entries(enablePivot.tableProps).forEach(([key, value]) => {
        if (key === 'externalChartContainer') {
          pivotDefaultProps.createChartContainer = createChartContainer(value)
        }
        pivotDefaultProps[key] = value
      })
    enablePivot.columnDefProps &&
      Object.entries(enablePivot.columnDefProps).forEach(([key, value]) => {
        pivotDefaultColDefProps[key] = value
      })
  }
  const pivotProps = enablePivot ? pivotDefaultProps : {}
  const pivotDefaultColProps = enablePivot ? pivotDefaultColDefProps : {}

  const defaultColDefProp = {
    sortable,
    filter,
    floatingFilter,
    editable,
    resizable,
    enableColResize: colResize,
    width,
    minWidth,
    flex,
    headerTooltip,
    ...defaultColDef,
    ...pivotDefaultColProps
  }

  const onGridReady = useCallback(
    (params) => {
      setGridApi(params.api)
      setColumnApi(params.columnApi)
      if (autoSizeColumns) {
        onFirstDataRendered(params)
      }
      if (onGridReadyProp) {
        onGridReadyProp(params)
      }
    },
    [autoSizeColumns, onGridReadyProp]
  )

  const handleRowsSelection = useCallback(
    (props) => {
      if (onRowSelectedProp) {
        const selectedData = props.api.getSelectedRows()
        onRowSelectedProp(selectedData)
      } else {
        onRowSelected(props)
      }
    },
    [onRowSelectedProp, onRowSelected]
  )

  const handleRowsSelectionChanged = useCallback(
    ({ api }) => {
      if (onSelectionChanged) {
        const selectedData = []
        api.forEachNodeAfterFilter(({ data, selected }) => {
          if (selected) selectedData.push(data)
        })
        onSelectionChanged(selectedData)
      }
    },
    [onSelectionChanged]
  )

  const handleAllRowEditing = useCallback(
    ({ api }) => {
      if (onFullRowEdit) {
        const cellDefs = api.getEditingCells()
        if (cellDefs.length > 0) {
          const cellDef = cellDefs[0]
          const rowIndex = cellDef.rowIndex
          const rowPin = cellDef.rowPinned
          const colKey = cellDef.column.getId()
          api.setFocusedCell(rowIndex, colKey)
          api.startEditingCell({
            rowIndex,
            colKey
          })
          onFullRowEdit({
            rowIndex: rowIndex,
            colKey: colKey,
            rowPin: rowPin
          })
        }
      }
    },
    [onFullRowEdit]
  )

  const onFirstDataRendered = useCallback((params) => {
    if (autoSizeColumnsOnFirstRender) {
      autoSizeAll(false, params.columnApi)

      const panel = params.api.gridPanel
      const availableWidth = panel.eBodyViewport.clientWidth
      const columns = panel.columnController.getAllDisplayedColumns()
      const usedWidth = panel.columnController.getWidthOfColsInList(columns)

      if (usedWidth < availableWidth) {
        params.api.sizeColumnsToFit()
      }
    }
    if (onFirstDataRenderedProp) {
      onFirstDataRenderedProp(params)
    }
  }, [])

  return (
    <div
      className={classNames('ublique-datatable', agTheme, className, {
        'max-height-with-scroll': maxHeightWithScroll
      })}
    >
      <TableHeader
        localeTextFunc={localeTextFunc}
        defaultActionsi18nKeys={defaultActionsi18nKeys}
        gridApi={gridApi}
        columnApi={columnApi}
        customActions={customActions}
        canDuplicate={canDuplicate}
        canAddRows={canAddRows}
        canDeleteRows={canDeleteRows}
        canClearAll={canClearAll}
        canShowCheckbox={canShowCheckbox}
        canPinRowTop={canPinRowTop}
        canPinRowBottom={canPinRowBottom}
        isSizeToFit={isSizeToFit}
        isAutoSize={isAutoSize}
        isAutoSizeAll={isAutoSizeAll}
        canExportCSV={canExportCSV}
        canSelectAll={canSelectAll}
        customComponent={extraHeaderComponent}
        customComponentContainerClassName={
          extraHeaderComponentContainerClassName
        }
        canSearch={canSearch}
        onRowAdd={onRowAdd}
        onRowDelete={onRowDelete}
      />
      <div
        className='ag-grid-table'
        style={minHeight ? { minHeight } : undefined}
      >
        <AgGridReact
          {...props}
          detailCellRendererParams={detailCellRendererParams}
          customActions={customActions}
          defaultColDef={defaultColDefProp}
          rowSelection={rowSelection}
          rowMultiSelectWithClick={rowMultiSelectWithClick}
          modules={modules}
          domLayout={domLayout}
          columnDefs={columnDefs}
          rowData={rowData}
          getRowStyle={getRowStyle}
          rowHeight={rowHeight}
          enableRangeSelection={enableRangeSelection}
          frameworkComponents={frameworkComponents}
          headerClass={headerClass}
          sortingOrder={sortingOrder}
          multiSortKey={multiSortKey}
          editType={editType}
          treeData={treeData}
          groupDefaultExpanded={groupDefaultExpanded}
          animateRows={animateRows}
          pagination={pagination}
          masterDetail={masterDetail}
          paginationPageSize={paginationPageSize}
          onGridReady={onGridReady}
          onSelectionChanged={handleRowsSelectionChanged}
          onRowSelected={handleRowsSelection}
          onRowEditingStarted={handleAllRowEditing}
          onFirstDataRendered={onFirstDataRendered}
          localeTextFunc={localeTextFunc}
          {...pivotProps}
        />
      </div>
    </div>
  )
}
DataTable.defaultProps = {
  domLayout: 'autoHeight',
  sortable: false,
  filter: false,
  floatingFilter: false,
  canAddRows: false,
  canDeleteRows: false,
  detailCellRendererParams: {},
  canAddDuplicate: false,
  canDuplicate: false,
  canClearAll: false,
  canShowCheckbox: false,
  canPinRowTop: false,
  canPinRowBottom: false,
  isSizeToFit: false,
  isAutoSize: false,
  isAutoSizeAll: false,
  autoGroupColumnDef: {},
  resizable: false,
  animateRows: false,
  pagination: false,
  masterDetail: false,
  hide: false,
  flex: null,
  rowHeight: 50,
  treeData: false,
  width: 150,
  minWidth: 100,
  tooltipShowDelay: 0,
  enableRangeSelection: false,
  sortingOrder: ['desc', 'asc', null],
  rowMultiSelectWithClick: false,
  editable: false,
  fullRowEdit: false,
  autoSizeColumns: false,
  canSearch: true,
  onRowSelected: () => {},
  maxHeightWithScroll: false
}

DataTable.propTypes = {
  /** The object to be used when displaying a nested table (available in Enterprise version) */
  detailCellRendererParams: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func
  ]),
  /** The Entry point to the datatable api, it provides access to the datatable api functionalities */
  onGridReady: PropTypes.func,
  /** A function to export the table data */
  onExportToCsv: PropTypes.func,
  /** An object to set default functions or parameters to pass to the table */
  frameworkComponents: PropTypes.object,
  /** A function that provides custom styles when performing certain table operations */
  getRowStyle: PropTypes.func,
  /** Controlls expanding and collapsing a colum group */
  groupDefaultExpanded: PropTypes.number,
  /** A string value that is used to select single or multiple rows */
  rowSelection: PropTypes.string,
  /** A boolean value that allows selecting multi rows with click */
  rowMultiSelectWithClick: PropTypes.bool,
  /** Could be either cell edit or full edit, it is used in the row data update */
  editType: PropTypes.string,
  /** It is a string that decides the table cell layout, default is autoHeight */
  domLayout: PropTypes.string,
  /** The column data to feed into the table */
  columnDefs: PropTypes.array,
  /** The row data to populate each column in the table */
  rowData: PropTypes.array,
  /** This is a value that decides the height of row cells */
  rowHeight: PropTypes.number,
  /** This is a boolean value that allows selecting a range of cells in the table */
  enableRangeSelection: PropTypes.bool,
  /** This is a string value that is passed for each header and is viewed as a popup message on mouse hover */
  headerTooltip: PropTypes.string,
  /** This property decides the header size */
  headerClass: PropTypes.string,
  /** This allows for a multi column sorting by holding the ctrl button */
  multiSortKey: PropTypes.string,
  /** This property enables the row animation on change or update */
  animateRows: PropTypes.bool,
  /** This is the pagination property */
  pagination: PropTypes.bool,
  /** This is to set the nested table view */
  masterDetail: PropTypes.bool,
  /** This is the pagination property, but with custom size */
  paginationPageSize: PropTypes.number,
  /** This property allows sorting each column */
  sortable: PropTypes.bool,
  /** This property allows filtering each column */
  filter: PropTypes.bool,
  /** This property allows showing a floating filter beneath each column */
  floatingFilter: PropTypes.bool,
  /** This property sets cells to be editable */
  editable: PropTypes.bool,
  /** Shows an input field to search inside the table. Enabled by default. */
  canSearch: PropTypes.bool,
  /** A boolean flag to enable adding new rows */
  canAddRows: PropTypes.bool,
  /** A boolean flag to enable deleting the rows */
  canDeleteRows: PropTypes.bool,
  /** A boolean flag to enable creating a new copy of the rows */
  canDuplicate: PropTypes.bool,
  /** A boolean flag to enable clearing all the table rows */
  canClearAll: PropTypes.bool,
  /** A boolean flag to enable showing/hiding a checkbox column in a table */
  canShowCheckbox: PropTypes.bool,
  /** A boolean flag to enable pinning rows to the top */
  canPinRowTop: PropTypes.bool,
  /** A boolean flag to enable pinning rows to the bottom */
  canPinRowBottom: PropTypes.bool,
  /** A boolean flag that sets table column to fixed size */
  isSizeToFit: PropTypes.bool,
  /** A boolean flag to enable setting the whole table to a custom size (including header) */
  isAutoSize: PropTypes.bool,
  /** A boolean flag to enable setting the whole table to a custom size (excluding header) */
  isAutoSizeAll: PropTypes.bool,
  /** An event that is triggered on row selection */
  onRowsSelect: PropTypes.func,
  /** An event that is triggered on row addition */
  onRowAdd: PropTypes.func,
  /** An event that is triggered on row duplication */
  onRowsDuplicate: PropTypes.func,
  /** An event that is triggered on row deletion */
  onRowDelete: PropTypes.func,
  /** An event that is triggered on all rows deletion */
  onAllRowsDelete: PropTypes.func,
  /** An event that is triggered on full row editing */
  onFullRowEdit: PropTypes.func,
  /** A property that sets the column to be resizable */
  colResize: PropTypes.bool,
  /** A property that sets the table to be resizable */
  resizable: PropTypes.bool,
  /** This is the property to decide the table with */
  width: PropTypes.number,
  /** This is the minimum table width, it is usually used as a default property */
  minWidth: PropTypes.number,
  /** Table minimum height, useful to avoid cutting off menus when there aren't enough rows */
  minHeight: PropTypes.number,
  /** This property is set to adjust the coulm width/height */
  flex: PropTypes.number,
  /** This value is set to enable the full row editing */
  fullRowEdit: PropTypes.bool,
  /** This boolean value checks and sets the table size */
  autoSizeColumns: PropTypes.bool,
  /** A custom component that will be rendered on the right-hand side of the table header. It will receive columnApi and gridApi as props */
  extraHeaderComponent: PropTypes.element,
  /** extraHeaderComponent container className */
  extraHeaderComponentContainerClassName: PropTypes.string,
  /** Custom default column def */
  defaultColDef: PropTypes.object,
  /** Custom CSS classname */
  className: PropTypes.string,
  /** Autosizes all columns as soon as data are rendered */
  autoSizeColumnsOnFirstRender: PropTypes.bool,
  /** Default actions i18n keys */
  defaultActionsi18nKeys: PropTypes.shape({
    exportCSV: PropTypes.string,
    duplicate: PropTypes.string,
    selectAll: PropTypes.string,
    delete: PropTypes.string,
    clearAll: PropTypes.string,
    pinRowTop: PropTypes.string,
    pinRowBottom: PropTypes.string,
    sizeToFit: PropTypes.string,
    autoSizeAll: PropTypes.string,
    autoSizeSkipHeader: PropTypes.string
  }),
  /** Enables pivot mode and all the linked props */
  enablePivot: PropTypes.object,
  /** Set the height of the table as the height of the page with the scrollbar inside */
  maxHeightWithScroll: PropTypes.bool
}

const pivotDefaultProps = {
  pivotMode: true,
  domLayout: 'normal',
  enableRangeSelection: true,
  enableRangeHandle: true,
  suppressMultiRangeSelection: false,
  rowMultiSelectWithClick: true,
  rowSelection: 'multiple',
  statusBar: statusPanel,
  rowGroupPanelShow: 'always',
  pivotPanelShow: 'always',
  enableCharts: true,
  sideBar: true,
  aggFuncs: aggFuncs
}

const pivotDefaultColDefProps = {
  sortable: true,
  filter: true,
  resizable: true
}
export { TableActions, DataTable as default }
